import { FunctionComponent, memo } from "react";
import { Field, Form, Formik } from "formik";
import { faSave } from "@fortawesome/pro-solid-svg-icons";
import { Button, Checkbox, FieldError } from "@edgetier/components";
import { trimObjectValues } from "@edgetier/utilities";
import * as Yup from "yup";

import SelectLanguage from "components-for/filters/select-language";
import SelectRole from "components-for/filters/select-role";
import SelectModel from "components-for/filters/select-model";
import SelectTimeZone from "components-for/filters/select-time-zone";
import { IUserFormValues, IProps } from "./user-form.types";
import "./user-form.scss";

/**
 * A form for inviting a new user to Watchtower.
 * @param props.initialValues   Form initial values.
 * @param props.onSubmit        An optional callback that will run when a user is successfully invited.
 */
const UserForm: FunctionComponent<IProps> = ({ initialValues, onSubmit }) => {
    /**
     * Trim the values before calling the onSubmit function.
     * @param values The details of the user to be invited.
     * @param errorCallback Optional callback action trigged on error.
     */
    const preSubmit = (values: IUserFormValues, errorCallback?: VoidFunction) => {
        const trimmedValues = trimObjectValues(values);
        onSubmit(trimmedValues, errorCallback);
    };

    return (
        <Formik<IUserFormValues>
            initialValues={initialValues}
            validationSchema={Yup.object().shape({
                alertsEnabled: Yup.boolean().required("Required"),
                firstName: Yup.string().trim().required("Required"),
                surname: Yup.string().trim().required("Required"),
                emailAddress: Yup.string().trim().required("Required"),
                languageIsoCode: Yup.string().nullable().required("Required"),
                roleId: Yup.string().nullable().required("Required"),
                modelIds: Yup.array().of(Yup.string()),
                timeZoneId: Yup.number().nullable().required("Required"),
            })}
            onSubmit={(values, { setSubmitting }) => preSubmit(values, () => setSubmitting(false))}
        >
            {({ isSubmitting, values }) => (
                <Form autoComplete="off" name="userForm">
                    <div className="field">
                        <FieldError name="firstName" />
                        <label htmlFor="firstName">First Name</label>
                        <Field id="firstName" name="firstName" type="text" placeholder="First name..." />
                    </div>
                    <div className="field">
                        <FieldError name="surname" />
                        <label htmlFor="surname">Surname</label>
                        <Field id="surname" name="surname" type="text" placeholder="Surname..." />
                    </div>
                    <div className="field">
                        <FieldError name="emailAddress" />
                        <label htmlFor="emailAddress">Email Address</label>
                        <Field id="emailAddress" name="emailAddress" type="email" placeholder="Email address..." />
                    </div>
                    <div className="field">
                        <FieldError name="languageIsoCode" />
                        <label htmlFor="languageIsoCode">Language</label>
                        <SelectLanguage isSingleSelect name="languageIsoCode" />
                    </div>
                    <div className="field">
                        <FieldError name="roleId" />
                        <label htmlFor="roleId">Role</label>
                        <SelectRole isSingleSelect name="roleId" />
                    </div>
                    <div className="field">
                        <FieldError name="modelIds" />
                        <label htmlFor="modelIds">Anomaly Email Notifications</label>
                        <SelectModel name="modelIds" isFilter={false} />
                    </div>
                    <div className="field">
                        <FieldError name="timeZoneId" />
                        <label htmlFor="timeZoneId">Time Zone (for times in email alerts)</label>
                        <SelectTimeZone placement="top-center" isSingleSelect name="timeZoneId" isClearable />
                    </div>
                    <div className="field">
                        <FieldError name="alertsEnabled" />
                        <Checkbox
                            label="Receive anomaly email alerts"
                            name="alertsEnabled"
                            checked={values.alertsEnabled ?? false}
                        />
                    </div>
                    <Button type="submit" isLoading={isSubmitting} icon={faSave}>
                        Submit
                    </Button>
                </Form>
            )}
        </Formik>
    );
};

export default memo(UserForm);
